import React, {
  useEffect,
  useState,
} from "react";

import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";

import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";

import {
  httpsCallable,
} from "firebase/functions";

import {
  Navigate,
} from "react-router-dom";

import {
  useSelector,
} from "react-redux";

import {
  db,
  functions,
} from "../../firebase/config";

const formatDate = (value) => {
  if (!value) {
    return "Not available";
  }

  const date =
    value.toDate?.() ||
    new Date(value);

  if (
    Number.isNaN(date.getTime())
  ) {
    return "Not available";
  }

  return date.toLocaleString();
};

const PendingUsers = () => {
  const toast = useToast();

  const [users, setUsers] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [loadError, setLoadError] =
    useState("");

  const [
    processingUser,
    setProcessingUser,
  ] = useState(null);

  const authUser = useSelector(
    (state) => state.auth.user
  );

  const profile = useSelector(
    (state) =>
      state.profile?.profile
  );

  const isAdmin =
    profile?.isAdmin === true ||
    authUser?.isAdmin === true;

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return undefined;
    }

    const pendingUsersQuery = query(
      collection(db, "users"),
      where(
        "approvalStatus",
        "==",
        "pending"
      )
    );

    const unsubscribe = onSnapshot(
      pendingUsersQuery,
      (snapshot) => {
        const pendingUsers =
          snapshot.docs
            .map((userDocument) => ({
              id: userDocument.id,
              ...userDocument.data(),
            }))
            .sort((first, second) => {
              const firstTime =
                first.createdAt
                  ?.toMillis?.() || 0;

              const secondTime =
                second.createdAt
                  ?.toMillis?.() || 0;

              return (
                firstTime -
                secondTime
              );
            });

        setUsers(pendingUsers);
        setLoadError("");
        setLoading(false);
      },
      (error) => {
        console.error(
          "Pending users listener failed:",
          {
            code:
              error.code || "unknown",
          }
        );

        setLoadError(
          "Account requests could not be loaded."
        );

        setLoading(false);
      }
    );

    return unsubscribe;
  }, [isAdmin]);

  const handleDecision = async (
    user,
    decision
  ) => {
    const actionLabel =
      decision === "approve"
        ? "approve"
        : "deny";

    const confirmed = window.confirm(
      `${actionLabel
        .charAt(0)
        .toUpperCase()}${actionLabel.slice(
        1
      )} access for ${
        user.displayName ||
        user.firstName ||
        user.email
      }?`
    );

    if (!confirmed) {
      return;
    }

    setProcessingUser({
      uid: user.id,
      decision,
    });

    try {
      const functionName =
        decision === "approve"
          ? "approveUser"
          : "denyUser";

      const callable =
        httpsCallable(
          functions,
          functionName
        );

      await callable({
        uid: user.id,
      });

      toast({
        title:
          decision === "approve"
            ? "Account approved"
            : "Account denied",
        description:
          decision === "approve"
            ? `${user.email} can now access the application.`
            : `${user.email} remains blocked.`,
        status:
          decision === "approve"
            ? "success"
            : "info",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error(
        "Account decision failed:",
        {
          code:
            error.code || "unknown",
        }
      );

      toast({
        title:
          "Account request could not be updated",
        description:
          error.message ||
          "Please try again.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setProcessingUser(null);
    }
  };

  if (!isAdmin) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }

  return (
    <Box
      p={{
        base: 4,
        md: 6,
      }}
      maxW="1000px"
      mx="auto"
    >
      <Heading
        size="lg"
        mb={2}
      >
        Account Requests
      </Heading>

      <Text
        color="gray.600"
        mb={6}
      >
        Review verified users requesting
        access to the Capital Project
        application.
      </Text>

      {loadError && (
        <Alert
          status="error"
          mb={5}
        >
          <AlertIcon />

          {loadError}
        </Alert>
      )}

      {loading ? (
        <Flex
          justify="center"
          py={10}
        >
          <Spinner size="xl" />
        </Flex>
      ) : users.length === 0 ? (
        <Box
          p={6}
          borderWidth="1px"
          borderRadius="md"
          bg="gray.50"
        >
          <Text color="gray.600">
            There are no pending account
            requests.
          </Text>
        </Box>
      ) : (
        <Stack spacing={4}>
          {users.map((user) => {
            const approving =
              processingUser?.uid ===
                user.id &&
              processingUser?.decision ===
                "approve";

            const denying =
              processingUser?.uid ===
                user.id &&
              processingUser?.decision ===
                "deny";

            return (
              <Box
                key={user.id}
                p={5}
                borderWidth="1px"
                borderRadius="lg"
                bg="white"
                shadow="sm"
              >
                <Flex
                  justify="space-between"
                  align={{
                    base: "flex-start",
                    md: "center",
                  }}
                  direction={{
                    base: "column",
                    md: "row",
                  }}
                  gap={4}
                >
                  <Stack spacing={1}>
                    <Flex
                      align="center"
                      gap={2}
                      flexWrap="wrap"
                    >
                      <Text fontWeight="bold">
                        {user.displayName ||
                          user.firstName ||
                          "New user"}
                      </Text>

                      <Badge colorScheme="yellow">
                        Pending
                      </Badge>
                    </Flex>

                    <Text>
                      {user.email}
                    </Text>

                    <Text
                      fontSize="sm"
                      color="gray.500"
                    >
                      Registered:{" "}
                      {formatDate(
                        user.createdAt
                      )}
                    </Text>

                    <Text
                      fontSize="sm"
                      color="gray.500"
                    >
                      Approval requested:{" "}
                      {formatDate(
                        user.approvalRequestedAt
                      )}
                    </Text>
                  </Stack>

                  <Flex
                    gap={3}
                    flexWrap="wrap"
                  >
                    <Button
                      colorScheme="green"
                      onClick={() =>
                        handleDecision(
                          user,
                          "approve"
                        )
                      }
                      isLoading={approving}
                      isDisabled={
                        processingUser !==
                        null
                      }
                    >
                      Approve
                    </Button>

                    <Button
                      colorScheme="red"
                      variant="outline"
                      onClick={() =>
                        handleDecision(
                          user,
                          "deny"
                        )
                      }
                      isLoading={denying}
                      isDisabled={
                        processingUser !==
                        null
                      }
                    >
                      Deny
                    </Button>
                  </Flex>
                </Flex>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default PendingUsers;